import { useEffect, useState } from "react";
import papi, { formatErr } from "@/lib/partnerApi";
import { SectionHead } from "./VdaShared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Radar, Loader2, CheckCircle2, Copy, Users, Info } from "lucide-react";

export default function VdaDiscover({ goTo }) {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ city: "Noida", state: "Uttar Pradesh", category: "Interior Designer", subcategory: "", target: 50 });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { papi.get("/vda/categories").then((r) => setCats(r.data.filter((c) => c.active))).catch(() => {}); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const run = async () => {
    if (!form.city || !form.category) { toast.error("City and category are required."); return; }
    const t = Number(form.target) || 50;
    if (t > 20 && !window.confirm(`This will pull up to ${t} businesses from Google Places (uses API quota). Continue?`)) return;
    setLoading(true); setResult(null);
    try {
      const { data } = await papi.post("/vda/discover", { ...form, target: t });
      setResult(data);
      toast.success(`Discovery complete — ${data.unique} unique prospects added.`);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl">
      <SectionHead title="Discover Vendors" desc="Pulls publicly listed construction businesses from Google Places. Real data only — nothing is invented." />

      <div className="mb-5 flex items-start gap-3 rounded-xl border border-teal-200 bg-teal-50 p-4 text-sm text-teal-800">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <p>Every prospect is sourced live from public Google Business listings. Missing fields are stored as “Not Available” — never guessed.</p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label>City *</Label><Input data-testid="disc-city" className="mt-1.5 rounded-xl" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="e.g. Noida" /></div>
          <div><Label>State</Label><Input data-testid="disc-state" className="mt-1.5 rounded-xl" value={form.state} onChange={(e) => set("state", e.target.value)} placeholder="e.g. Uttar Pradesh" /></div>
          <div>
            <Label>Category *</Label>
            <select data-testid="disc-category" className="mt-1.5 h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm" value={form.category} onChange={(e) => set("category", e.target.value)}>
              {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div><Label>Subcategory (optional)</Label><Input data-testid="disc-subcategory" className="mt-1.5 rounded-xl" value={form.subcategory} onChange={(e) => set("subcategory", e.target.value)} placeholder="e.g. Luxury" /></div>
          <div><Label>Target count</Label><Input data-testid="disc-target" type="number" min="1" max="60" className="mt-1.5 rounded-xl" value={form.target} onChange={(e) => set("target", e.target.value)} /></div>
        </div>
        <Button data-testid="disc-run" onClick={run} disabled={loading} className="mt-6 h-11 rounded-xl bg-teal-500 px-6 hover:bg-teal-600">
          {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Discovering…</> : <><Radar className="mr-2 h-4 w-4" /> Start Discovery</>}
        </Button>
        {loading && <p className="mt-3 text-xs text-slate-400">Querying Google Places & enriching phone/website details — this can take up to a minute for 50 vendors.</p>}
      </div>

      {result && (
        <div data-testid="disc-result" className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
          <div className="flex items-center gap-2 text-emerald-800"><CheckCircle2 className="h-5 w-5" /><span className="font-display font-bold">Discovery Complete</span></div>
          <div className="mt-4 grid grid-cols-3 gap-4">
            <Stat icon={Users} label="Discovered" value={result.discovered} />
            <Stat icon={CheckCircle2} label="Unique Added" value={result.unique} />
            <Stat icon={Copy} label="Duplicates" value={result.duplicates} />
          </div>
          <Button data-testid="disc-view-prospects" onClick={() => goTo("prospects")} variant="outline" className="mt-5 rounded-xl border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-100">View Prospects →</Button>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-white p-4 text-center">
      <Icon className="mx-auto h-5 w-5 text-emerald-600" />
      <p className="mt-2 text-2xl font-extrabold tabular-nums text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}
